import { useEnvironmentVariables } from 'app-kit'
import { useGeckoAnalytics } from 'features/analytics'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import { updateMarkup } from 'features/kestrelViewer/utils/updateMarkup'
import { useRolodexEntries } from 'features/rolodex'
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Vector2 } from 'three'
import {
  DropdownOption,
  GeckoKestrelModel,
  GeckoKestrelModelCameras,
  GeckoKestrelModelUnit,
  GeckoUnit,
  KestrelGatekeeper,
  KestrelInspectionType,
  KVAnnotation,
  KVCameraPose,
  KVCameraView,
  KVInspectionArea,
  KVMarkup,
  KVMeasurement,
  KVPlaneIntersection,
  KVPrimitive,
} from 'types'
import { useMemoizedObject } from 'utils/hookHelpers'

import { resolveGatekeeper } from './config/gatekeeper'
import { useCameraPoses } from './hooks/useCameraPoses'
import { useHistoricalAnnotations } from './hooks/useHistoricalAnnotations'
import { useInspectionAreaWorkOrders } from './hooks/useInspectionAreaWorkOrders'
import { useKestrelMenuUnitData } from './hooks/useKestrelMenuUnitData'
import { useTagOptions } from './hooks/useTagOptions'
import { KestrelCommands } from './types/KestrelCommands'
import { LOADING_STEP_ORDER, type LoadingStep } from './types/kestrelLoading'

/**
 * An object containing various states for the Kestrel viewer stream.
 */
export interface KestrelStatus {
  /** Whether the photogrammetry toggle is pressed. */
  toggledOn?: boolean
  /** Whether the connection should remain open (persists for some time after toggle is off). */
  shouldConnect?: boolean
  /** Whether the connection has been established and video is ready to be displayed. */
  videoReady?: boolean
  /** Whether Kestrel is currently loading - this means that the a scene has been requested,
   * but the video is not yet ready. */
  isLoading: boolean
  /** This represent a disruptive error that prevents the Kestrel from working - this will be displayed to the user. */
  error: string | null
  /** Whether the loading view timed out waiting for video readiness. */
  hasTimedOut: boolean
  /** Whether the blur overlay should be shown due to user inactivity. */
  showBlur: boolean
  /** The UUID of the provisioned VM, if known - slightly different from the pixel streaming URL. */
  vmID: string | null
  /** Whether the VM was deleted (e.g., due to inactivity). */
  isVmDeleted: boolean
  /** Current loading step in the initialization pipeline. */
  loadingStep?: LoadingStep
  /** Identifier for the active loading attempt. */
  loadingSequence: number
  /** Whether we're resetting the connection in the background. */
  isResetting: boolean
}

/**
 * KestrelContext: Persisted model data and connection lifecycle.
 *
 * Use this for:
 * - Model data stored in Rolodex (annotations, measurements, cameraViews, etc.)
 * - Connection status and pixel streaming URL
 * - Site/org context and unit relationships
 * - Feature gatekeeper configuration
 *
 * Do NOT use for ephemeral UI state—use KestrelViewerContext instead.
 */
export type KestrelContextType = {
  /** The Kestrel Model Entry for the current component. */
  kestrelModel: GeckoKestrelModel | undefined

  kestrelModelCameras: GeckoKestrelModelCameras | undefined

  /** Function to set the Kestrel Model Entry for the current component. */
  setKestrelModel: React.Dispatch<
    React.SetStateAction<GeckoKestrelModel | undefined>
  >

  /** Convenience getter for the model's inspection type. */
  inspectionType: KestrelInspectionType
  /** Resolved feature gatekeeper for the current inspection type and model. */
  gatekeeper: KestrelGatekeeper

  /** Convenience tags for currently selected Kestrel model */
  siteId?: string
  orgId?: string

  /** Units available at the model's site */
  siteUnits: ReadonlyArray<GeckoUnit>

  /** Dropdown options for the site's units */
  unitOptions: DropdownOption[]

  /** Links between this Kestrel model and units (kestrel-model-unit entries) */
  kestrelModelUnits: ReadonlyArray<GeckoKestrelModelUnit>

  /** Indicates whether we're connected to a custom windows dev pixel streaming VM */
  devMode: boolean

  /** Function to store the URL the pixel streaming instance connects to in session storage */
  setStoredPixelStreamingURL: (value: string | undefined) => void

  /** Authorized axios instance for API calls. */
  authorizedAxios: AuthedAxiosRequests

  /** A collection of values to monitor the Kestrel viewer stream configuration and connection. */
  kestrelStatus: KestrelStatus

  /** Set the collection of values to monitor the Kestrel viewer stream configuration and connection. */
  setKestrelStatus: Dispatch<React.SetStateAction<KestrelStatus>>

  /** Typed Helpers for setting the Kestrel status. */
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  setHasTimedOut: (v: boolean) => void
  setBlurVisible: (v: boolean) => void
  setVmID: (id: string | null) => void
  setVmDeleted: (v: boolean) => void
  setVideoReady: (v: boolean) => void
  startLoadingSequence: () => number
  setLoadingStep: (
    step: KestrelStatus['loadingStep'],
    sequence?: number
  ) => void
  setResetting: (v: boolean) => void

  /** Currently selected Kestrel Viewer markup. */
  selectedMarkup: KVMarkup | undefined

  /** Function to set the currently selected Kestrel Viewer markup. */
  setSelectedMarkup: Dispatch<SetStateAction<KVMarkup | undefined>>

  /** Function to send a json message to the Kestrel Viewer stream. */
  sendKVCommand: ((json: object | string) => void) | undefined

  /** Function to set the function to send a json message to the Kestrel Viewer stream. */
  setSendKVCommand: Dispatch<
    SetStateAction<((json: object | string) => void) | undefined>
  >

  /** The starting position of the popup menu. */
  popupMenuStartPosition: Vector2

  /** Function to set the starting position of the popup menu. */
  setPopupMenuStartPosition: Dispatch<SetStateAction<Vector2>> | undefined

  /** The current pixel streaming URL for Kestrel viewer. */
  pixelStreamingURL: string

  /** Function to set the pixel streaming URL. */
  setPixelStreamingURL: React.Dispatch<React.SetStateAction<string>>

  // Model data arrays
  annotations: KVAnnotation[] | undefined
  setAnnotations: Dispatch<SetStateAction<KVAnnotation[] | undefined>>

  // Past annotations on the model
  historicalAnnotations: KVAnnotation[] | undefined
  setHistoricalAnnotations: Dispatch<SetStateAction<KVAnnotation[] | undefined>>

  measurements: KVMeasurement[] | undefined
  setMeasurements: Dispatch<SetStateAction<KVMeasurement[] | undefined>>

  planeIntersections: KVPlaneIntersection[] | undefined
  setPlaneIntersections: Dispatch<
    SetStateAction<KVPlaneIntersection[] | undefined>
  >
  cameraViews: KVCameraView[] | undefined
  setCameraViews: Dispatch<SetStateAction<KVCameraView[] | undefined>>
  cameraPoses: KVCameraPose[] | undefined
  setCameraPoses: Dispatch<SetStateAction<KVCameraPose[] | undefined>>
  inspectionAreas: KVInspectionArea[] | undefined
  setInspectionAreas: Dispatch<SetStateAction<KVInspectionArea[] | undefined>>

  annotationSortKey: string
  setAnnotationSortKey: Dispatch<SetStateAction<string>>

  derivedTags: string[]

  primitives: KVPrimitive[] | undefined
  setPrimitives: Dispatch<SetStateAction<KVPrimitive[] | undefined>>

  // Model update functions
  updateAnnotation: (
    annotation: KVAnnotation,
    jsonField: string | string[],
    setter: (a: KVAnnotation) => KVAnnotation,
    v: unknown | unknown[]
  ) => void

  updateHistoricalAnnotations: (
    annotation: KVAnnotation,
    jsonField: string | string[],
    setter: (a: KVAnnotation) => KVAnnotation,
    v: unknown | unknown[]
  ) => void

  updateMeasurement: (
    measurement: KVMeasurement,
    jsonField: string | string[],
    setter: (a: KVMeasurement) => KVMeasurement,
    v: string | boolean | (string | boolean)[]
  ) => void

  updatePlaneIntersection: (
    planeIntersection: KVPlaneIntersection,
    jsonField: string | string[],
    setter: (a: KVPlaneIntersection) => KVPlaneIntersection,
    v: unknown | unknown[]
  ) => void

  updateInspectionArea: (
    inspectionArea: KVInspectionArea,
    jsonField: string | string[],
    setter: (ia: KVInspectionArea) => KVInspectionArea,
    v: unknown | unknown[]
  ) => void

  updatePrimitive: (
    primitive: KVPrimitive,
    jsonField: string | string[],
    setter: (p: KVPrimitive) => KVPrimitive,
    v: unknown | unknown[]
  ) => void

  // Tag options for autocomplete (computed from annotations)
  tagOptions: Record<string, Set<string>>

  // Value overrides for tag values (e.g., inspection area IDs to labels)
  valueOverrides: Record<string, string>
}

const KestrelContext = React.createContext<KestrelContextType | undefined>(
  undefined
)

export const KestrelContextProvider: React.FC<
  KestrelContextType & {
    children: React.ReactNode
  }
> = ({ children, ...props }) => {
  const context = useMemoizedObject(props)
  return (
    <KestrelContext.Provider value={context}>
      {children}
    </KestrelContext.Provider>
  )
}

export const KestrelProvider: React.FC<{
  kestrelModels: ReadonlyArray<GeckoKestrelModel>
  children: React.ReactNode
}> = ({ kestrelModels, children }) => {
  const { authorizedAxios } = useGeckoAuth()
  const { trackButtonClicked } = useGeckoAnalytics()
  const { IS_LOCAL, IS_DEV } = useEnvironmentVariables()

  // Basic Kestrel state
  const [kestrelModel, setKestrelModel] = useState<
    GeckoKestrelModel | undefined
  >(undefined)

  const [kestrelModelCamerasId, setKestrelModelCamerasId] = useState<
    string | undefined
  >(undefined)

  const kestrelModelCameras = useRolodexEntries(
    kestrelModelCamerasId !== undefined
      ? { ids: [kestrelModelCamerasId] }
      : undefined
  ).getEntry(kestrelModelCamerasId) as GeckoKestrelModelCameras | undefined

  React.useEffect(() => {
    if (kestrelModels.length > 0) {
      const firstModel = kestrelModels.at(0)
      setKestrelModel(firstModel)
      const camerasId = firstModel?.tags.camerasEntry
      if (camerasId !== undefined && camerasId !== '') {
        setKestrelModelCamerasId(camerasId)
      }

      // Track the auto-start when we first get a model
      trackButtonClicked({
        button_name: 'kestrel_auto_start',
        kestrel_model_id: firstModel?.data.kestrel_model_id,
      })
    }
  }, [kestrelModels, trackButtonClicked])

  const [kestrelStatus, setKestrelStatus] = useState<KestrelStatus>({
    toggledOn: false,
    shouldConnect: false,
    videoReady: false,
    isLoading: false,
    error: null,
    hasTimedOut: false,
    showBlur: false,
    vmID: null,
    isVmDeleted: false,
    loadingStep: 'idle',
    loadingSequence: 0,
    isResetting: false,
  })

  const [devMode, setDevMode] = useState<boolean>(
    () =>
      (IS_LOCAL || IS_DEV) && Boolean(localStorage.getItem('pixelStreamingURL'))
  )

  const setStoredPixelStreamingURL = (value: string | undefined) => {
    const newUrl = value ?? ''

    setDevMode(Boolean(value))
    localStorage.setItem('pixelStreamingURL', newUrl)
    setPixelStreamingURL(newUrl)
  }

  // auto start Kestrel if we have models, using useEffect in case it takes time for the model avail. to load
  React.useEffect(() => {
    if (kestrelModels.length > 0 && !kestrelStatus.toggledOn) {
      setKestrelStatus((prev) => ({
        ...prev,
        toggledOn: true,
        shouldConnect: true,
        videoReady: false,
      }))
      trackButtonClicked({
        button_name: 'kestrel_auto_start',
        kestrel_model_id: kestrelModel?.data.kestrel_model_id,
      })
    }
  }, [
    kestrelModels.length,
    kestrelStatus.toggledOn,
    trackButtonClicked,
    kestrelModel?.data.kestrel_model_id,
  ])

  const [selectedMarkup, setSelectedMarkup] = useState<KVMarkup | undefined>(
    undefined
  )

  const [sendKVCommand, setSendKVCommand] = useState<
    ((json: object | string) => void) | undefined
  >(undefined)

  const [popupMenuStartPosition, setPopupMenuStartPosition] = useState<Vector2>(
    new Vector2(0, 0)
  )

  const [pixelStreamingURL, setPixelStreamingURL] = useState<string>(
    () => localStorage.getItem('pixelStreamingURL') || ''
  )

  // Model data arrays
  const [annotations, setAnnotations] = useState<KVAnnotation[] | undefined>(
    undefined
  )

  const [historicalAnnotations, setHistoricalAnnotations] = useState<
    KVAnnotation[] | undefined
  >(undefined)

  const [measurements, setMeasurements] = useState<KVMeasurement[] | undefined>(
    undefined
  )

  const [planeIntersections, setPlaneIntersections] = useState<
    KVPlaneIntersection[] | undefined
  >(undefined)

  const [cameraViews, setCameraViews] = useState<KVCameraView[] | undefined>(
    undefined
  )

  const [cameraPoses, setCameraPoses] = useState<KVCameraPose[] | undefined>(
    undefined
  )

  const [inspectionAreas, setInspectionAreas] = useState<
    KVInspectionArea[] | undefined
  >(undefined)

  const [primitives, setPrimitives] = useState<KVPrimitive[] | undefined>(
    undefined
  )

  // Initialize model data arrays from kestrelModel when it changes
  React.useEffect(() => {
    setAnnotations(kestrelModel?.data.annotations ?? [])
    setMeasurements(kestrelModel?.data.measurements ?? [])
    setPlaneIntersections(kestrelModel?.data.planeIntersections ?? [])
    setCameraViews(kestrelModel?.data.cameraViews ?? [])
    setInspectionAreas(kestrelModel?.data.inspectionAreas ?? [])
    setPrimitives(kestrelModel?.data.primitives ?? [])
  }, [
    kestrelModel?.id,
    kestrelModel?.data.annotations,
    kestrelModel?.data.measurements,
    kestrelModel?.data.planeIntersections,
    kestrelModel?.data.cameraViews,
    kestrelModel?.data.inspectionAreas,
    kestrelModel?.data.primitives,
  ])

  // Historical annotations from previous inspections
  const { fetchedHistoricalAnnotations } =
    useHistoricalAnnotations(kestrelModel)

  React.useEffect(() => {
    setHistoricalAnnotations(fetchedHistoricalAnnotations)
  }, [fetchedHistoricalAnnotations])

  // get camera poses from Rolodex CSV file
  const { cameraPoses: fetchedCameraPoses } = useCameraPoses(kestrelModel?.id)

  React.useEffect(() => {
    setCameraPoses(fetchedCameraPoses)
  }, [fetchedCameraPoses])

  // Tag options computed from annotations
  const { tagOptions, valueOverrides, derivedTags } = useTagOptions({
    annotations,
    inspectionAreas,
  })

  // Work order counts per inspection area
  const { workOrdersMap } = useInspectionAreaWorkOrders(
    kestrelModel?.id,
    annotations,
    inspectionAreas
  )

  // Sync work order counts to inspection areas and send to Unreal
  React.useEffect(() => {
    if (!inspectionAreas) return

    const changes: Array<{ id: string; count: number }> = []
    const updated = inspectionAreas.map((ia) => {
      const count = workOrdersMap[ia.id]?.length ?? 0
      if (ia.workOrderCount !== count) {
        changes.push({ id: ia.id, count })
        return { ...ia, workOrderCount: count }
      }
      return ia
    })

    if (changes.length > 0) {
      setInspectionAreas(updated)
      // Send updates to Unreal if video is ready
      if (sendKVCommand && kestrelStatus?.videoReady) {
        changes.forEach(({ id, count }) => {
          sendKVCommand({
            command: KestrelCommands.UPDATE_INSPECTION_AREA,
            inspectionAreaId: id,
            inspectionAreaWorkOrderCount: count,
          })
        })
      }
    }
  }, [workOrdersMap, inspectionAreas, sendKVCommand, kestrelStatus?.videoReady])

  const [annotationSortKey, setAnnotationSortKey] =
    useState<string>('inspectionArea')

  // Dependencies for update functions
  const { rolodexPatch } = useRolodexEdit()

  // ----------------------------------------------------------------------------
  // Shared Unit/Link data for menus
  const { siteId, orgId, siteUnits, unitOptions, kestrelModelUnits } =
    useKestrelMenuUnitData(kestrelModel)

  // Typed helper setters for consolidated status
  const setLoading = React.useCallback((isLoading: boolean) => {
    setKestrelStatus((prev) => ({ ...prev, isLoading }))
  }, [])

  const setError = React.useCallback((error: string | null) => {
    setKestrelStatus((prev) => ({ ...prev, error }))
  }, [])

  const clearError = React.useCallback(() => {
    setKestrelStatus((prev) => ({ ...prev, error: null }))
  }, [])

  const setHasTimedOut = React.useCallback((v: boolean) => {
    setKestrelStatus((prev) => ({ ...prev, hasTimedOut: v }))
  }, [])

  const setBlurVisible = React.useCallback((v: boolean) => {
    setKestrelStatus((prev) => ({ ...prev, showBlur: v }))
  }, [])

  const setVmID = React.useCallback((id: string | null) => {
    setKestrelStatus((prev) => ({ ...prev, vmID: id }))
  }, [])

  const setVmDeleted = React.useCallback((v: boolean) => {
    setKestrelStatus((prev) => ({ ...prev, isVmDeleted: v }))
  }, [])

  const setVideoReady = React.useCallback((v: boolean) => {
    setKestrelStatus((prev) => ({ ...prev, videoReady: v }))
  }, [])

  const startLoadingSequence = React.useCallback(() => {
    let sequence = 0
    setKestrelStatus((prev) => {
      sequence = prev.loadingSequence + 1
      return {
        ...prev,
        loadingSequence: sequence,
        loadingStep: 'reserving-vm',
      }
    })
    return sequence
  }, [])

  const setLoadingStep = React.useCallback(
    (step: KestrelStatus['loadingStep'], sequence?: number) => {
      setKestrelStatus((prev) => {
        if (sequence && sequence !== prev.loadingSequence) {
          return prev
        }

        const nextStep = step ?? undefined
        if (!nextStep) {
          return { ...prev, loadingStep: undefined }
        }

        const nextIndex = LOADING_STEP_ORDER.indexOf(nextStep)
        if (nextIndex === -1) {
          return prev
        }

        const currentStep = prev.loadingStep
        if (!currentStep) {
          return { ...prev, loadingStep: nextStep }
        }

        const currentIndex = LOADING_STEP_ORDER.indexOf(currentStep)
        if (nextIndex < currentIndex) {
          return prev
        }

        return { ...prev, loadingStep: nextStep }
      })
    },
    []
  )

  const setResetting = React.useCallback((v: boolean) => {
    setKestrelStatus((prev) => ({ ...prev, isResetting: v }))
  }, [])

  // Update functions
  const updateAnnotation = useMemo(
    () =>
      (
        annotation: KVAnnotation,
        jsonFields: string | string[],
        setter: (a: KVAnnotation) => KVAnnotation,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: annotation,
          jsonFields,
          setter,
          v,
          commandType: KestrelCommands.UPDATE_ANNOTATION,
          markupArray: annotations,
          setMarkupArray: setAnnotations,
          rolodexField: 'annotations',
          toJSON: KVAnnotation.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      annotations,
      setAnnotations,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updateHistoricalAnnotations = useMemo(
    () =>
      (
        annotation: KVAnnotation,
        jsonFields: string | string[],
        setter: (a: KVAnnotation) => KVAnnotation,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: annotation,
          jsonFields,
          setter,
          v,
          commandType:
            annotation.geometry?.kind === 'point'
              ? KestrelCommands.UPDATE_ANNOTATION
              : KestrelCommands.UPDATE_SURFACE_AREA,
          markupArray: historicalAnnotations,
          setMarkupArray: setHistoricalAnnotations,
          toJSON: KVAnnotation.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
        })
      },
    [sendKVCommand, historicalAnnotations, selectedMarkup, kestrelModel?.id]
  )

  const updateMeasurement = useMemo(
    () =>
      (
        measurement: KVMeasurement,
        jsonFields: string | string[],
        setter: (a: KVMeasurement) => KVMeasurement,
        v: string | boolean | (string | boolean)[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: measurement,
          jsonFields,
          setter,
          v,
          commandType: KestrelCommands.UPDATE_MEASUREMENT,
          markupArray: measurements,
          setMarkupArray: setMeasurements,
          rolodexField: 'measurements',
          toJSON: KVMeasurement.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      measurements,
      setMeasurements,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updatePlaneIntersection = useMemo(
    () =>
      (
        planeIntersection: KVPlaneIntersection,
        jsonFields: string | string[],
        setter: (a: KVPlaneIntersection) => KVPlaneIntersection,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: planeIntersection,
          jsonFields,
          setter,
          v,
          commandType: KestrelCommands.UPDATE_PLANE_INTERSECTION,
          markupArray: planeIntersections,
          setMarkupArray: setPlaneIntersections,
          rolodexField: 'planeIntersections',
          toJSON: KVPlaneIntersection.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      planeIntersections,
      setPlaneIntersections,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updateInspectionArea = useMemo(
    () =>
      (
        inspectionArea: KVInspectionArea,
        jsonFields: string | string[],
        setter: (ia: KVInspectionArea) => KVInspectionArea,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: inspectionArea,
          jsonFields,
          setter,
          v,
          commandType: KestrelCommands.UPDATE_INSPECTION_AREA,
          markupArray: inspectionAreas,
          setMarkupArray: setInspectionAreas,
          rolodexField: 'inspectionAreas',
          toJSON: KVInspectionArea.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      inspectionAreas,
      setInspectionAreas,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updatePrimitive = useMemo(
    () =>
      (
        primitive: KVPrimitive,
        jsonFields: string | string[],
        setter: (p: KVPrimitive) => KVPrimitive,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: primitive,
          jsonFields,
          setter,
          v,
          commandType: KestrelCommands.UPDATE_PRIMITIVE,
          markupArray: primitives,
          setMarkupArray: setPrimitives,
          rolodexField: 'primitives',
          toJSON: KVPrimitive.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      primitives,
      setPrimitives,
      rolodexPatch,
      kestrelModel,
    ]
  )

  return (
    <KestrelContextProvider
      {...{
        kestrelModel,
        setKestrelModel,
        kestrelModelCameras,
        inspectionType: kestrelModel?.data?.inspection_type as
          | KestrelInspectionType
          | KestrelInspectionType.DEFAULT,
        gatekeeper: resolveGatekeeper(
          (kestrelModel?.data?.inspection_type as KestrelInspectionType) ??
            KestrelInspectionType.DEFAULT,
          kestrelModel
        ),
        authorizedAxios,
        kestrelStatus,
        setKestrelStatus,
        siteId,
        orgId,
        siteUnits,
        unitOptions,
        kestrelModelUnits,
        setLoading,
        setError,
        clearError,
        setHasTimedOut,
        setBlurVisible,
        setVmID,
        setVmDeleted,
        setVideoReady,
        startLoadingSequence,
        setLoadingStep,
        setResetting,
        devMode,
        setStoredPixelStreamingURL,
        selectedMarkup,
        setSelectedMarkup,
        sendKVCommand,
        setSendKVCommand,
        popupMenuStartPosition,
        setPopupMenuStartPosition,
        pixelStreamingURL,
        setPixelStreamingURL,
        annotations,
        setAnnotations,
        updateAnnotation,
        historicalAnnotations,
        setHistoricalAnnotations,
        updateHistoricalAnnotations,
        measurements,
        setMeasurements,
        updateMeasurement,
        planeIntersections,
        setPlaneIntersections,
        updatePlaneIntersection,
        cameraViews,
        setCameraViews,
        cameraPoses,
        setCameraPoses,
        inspectionAreas,
        setInspectionAreas,
        updateInspectionArea,
        primitives,
        setPrimitives,
        updatePrimitive,
        tagOptions,
        valueOverrides,
        annotationSortKey,
        setAnnotationSortKey,
        derivedTags,
      }}>
      {children}
    </KestrelContextProvider>
  )
}

/**
 * A hook for accessing data from the Kestrel context
 */
// Intended to capture pre-connection kestrel data
export const useKestrel = (): KestrelContextType => {
  const context = useContext(KestrelContext)
  if (!context)
    throw new Error('useKestrel() must be used within a Kestrel context')
  return context
}
